import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const FRIENDLY_TIMEZONES = {
  'Asia/Kolkata': 'Asia/Kolkata (India Standard Time - IST / Kolkata / New Delhi)',
  'Asia/Calcutta': 'Asia/Calcutta (India Standard Time - IST / Kolkata / New Delhi)',
  'UTC': 'UTC (Coordinated Universal Time)',
  'Europe/London': 'Europe/London (United Kingdom - UK / GMT / BST)',
  'America/New_York': 'America/New_York (US Eastern Time - New York)',
  'America/Chicago': 'America/Chicago (US Central Time - Chicago)',
  'America/Denver': 'America/Denver (US Mountain Time - Denver)',
  'America/Los_Angeles': 'America/Los_Angeles (US Pacific Time - Los Angeles)',
  'America/Toronto': 'America/Toronto (Canada Eastern Time - Toronto)',
  'Europe/Paris': 'Europe/Paris (France - CET / CEST)',
  'Europe/Berlin': 'Europe/Berlin (Germany - CET / CEST)',
  'Asia/Tokyo': 'Asia/Tokyo (Japan Standard Time - JST)',
  'Asia/Singapore': 'Asia/Singapore (Singapore Time - SGT)',
  'Asia/Dubai': 'Asia/Dubai (United Arab Emirates - UAE / GST)',
  'Australia/Sydney': 'Australia/Sydney (Australia Eastern Time)',
};

const TOP_TIMEZONE_KEYS = [
  'Asia/Kolkata',
  'UTC',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'America/Toronto',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney'
];

export default function UserSettingsDialog({ open, onClose }) {
  const { user, updateUserPreferences } = useAuth();
  const [timezone, setTimezone] = useState('UTC');
  const [localePreference, setLocalePreference] = useState('en-GB');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [allTzStrings, setAllTzStrings] = useState(TOP_TIMEZONE_KEYS);

  useEffect(() => {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      try {
        const tzs = Intl.supportedValuesOf('timeZone');
        if (tzs && tzs.length > 0) {
          setAllTzStrings(tzs);
        }
      } catch (e) {
        console.warn('Could not load Intl supported timezones:', e);
      }
    }
  }, []);

  // Build options array, placing common/friendly timezones at the top
  const timezoneOptions = useMemo(() => {
    const optionsMap = new Map();

    // 1. Add top timezones first
    TOP_TIMEZONE_KEYS.forEach((tz) => {
      optionsMap.set(tz, {
        id: tz,
        label: FRIENDLY_TIMEZONES[tz] || tz,
      });
    });

    // 2. Add remaining timezones from Intl
    allTzStrings.forEach((tz) => {
      if (!optionsMap.has(tz)) {
        optionsMap.set(tz, {
          id: tz,
          label: FRIENDLY_TIMEZONES[tz] ? FRIENDLY_TIMEZONES[tz] : tz,
        });
      }
    });

    return Array.from(optionsMap.values());
  }, [allTzStrings]);

  useEffect(() => {
    if (open && user) {
      const savedTz = user.timezone || localStorage.getItem('timezone') || 'UTC';
      // Handle legacy Asia/Calcutta if present by mapping to Asia/Kolkata
      const normalizedTz = savedTz === 'Asia/Calcutta' ? 'Asia/Kolkata' : savedTz;
      setTimezone(normalizedTz);
      setLocalePreference(user.localePreference || localStorage.getItem('localePreference') || 'en-GB');
      setSuccessMsg('');
      setErrorMsg('');
    }
  }, [open, user]);

  const handleSave = async () => {
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const res = await updateUserPreferences(timezone, localePreference);
      if (res.success) {
        setSuccessMsg('Preferences updated successfully!');
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setErrorMsg(res.error || 'Failed to update preferences.');
      }
    } catch (e) {
      setErrorMsg('Error updating preferences.');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDetect = () => {
    try {
      let detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) {
        if (detected === 'Asia/Calcutta') detected = 'Asia/Kolkata';
        setTimezone(detected);
      }
    } catch (e) {
      console.warn('Auto detect failed:', e);
    }
  };

  const selectedTimezoneOption = useMemo(() => {
    return timezoneOptions.find((o) => o.id === timezone) || {
      id: timezone,
      label: FRIENDLY_TIMEZONES[timezone] || timezone,
    };
  }, [timezoneOptions, timezone]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Account Settings & Preferences</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize your time zone and date display formatting. This overrides browser defaults so timestamps look consistent across devices.
        </Typography>

        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Timezone</Typography>
              <Button size="small" variant="text" onClick={handleAutoDetect}>
                Auto-detect Device Timezone
              </Button>
            </Box>
            <Autocomplete
              value={selectedTimezoneOption}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  setTimezone(newValue.id);
                } else if (typeof newValue === 'string') {
                  setTimezone(newValue);
                }
              }}
              options={timezoneOptions}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
              isOptionEqualToValue={(option, value) => option.id === (value?.id || value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search or Select Timezone (e.g. India, Kolkata, UTC...)"
                  size="small"
                  fullWidth
                />
              )}
              ListboxProps={{ style: { maxHeight: 260 } }}
              clearOnEscape={false}
              disableClearable
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Date & Time Format (Locale)</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select Format</InputLabel>
              <Select
                value={localePreference}
                label="Select Format"
                onChange={(e) => setLocalePreference(e.target.value)}
              >
                <MenuItem value="en-GB">
                  Day / Month / Year (24-hour clock) — e.g. 21/07/2026, 14:30
                </MenuItem>
                <MenuItem value="en-US">
                  Month / Day / Year (12-hour clock) — e.g. 07/21/2026, 2:30 PM
                </MenuItem>
                <MenuItem value="en-CA">
                  Year - Month - Day (ISO format) — e.g. 2026-07-21, 14:30
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
